import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "@/components/feature/AdminLayout";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import LoadingState from "@/components/base/LoadingState";
import StatusBadge from "@/components/base/StatusBadge";
import OverviewTab    from "./tabs/OverviewTab";
import PortalAccessTab from "./tabs/PortalAccessTab";
import ContractsTab   from "./tabs/ContractsTab";
import JobsTab        from "./tabs/JobsTab";
import PaymentsTab    from "./tabs/PaymentsTab";
import MessagesTab    from "./tabs/MessagesTab";
import AddOnsTab      from "./tabs/AddOnsTab";
import { TABS, type Tab, type Client } from "./types";

export default function ClientDetailPage() {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();

  const [tenantId, setTenantId] = useState<string | null>(null);
  const [client, setClient]     = useState<Client | null>(null);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("overview");

  // Counts for badges
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [contractsCount, setContractsCount] = useState<number | null>(null);
  const [jobsCount, setJobsCount]           = useState<number | null>(null);

  const fetchClient = useCallback(async (tid: string, cid: string) => {
    setLoading(true);
    const [clientRes, msgCount, contractCount, jobCount] = await Promise.all([
      supabase.from("clients").select("*").eq("id", cid).eq("tenant_id", tid).maybeSingle(),
      supabase.from("client_messages").select("id", { count: "exact", head: true })
        .eq("client_id", cid).eq("tenant_id", tid).eq("sender_type", "client").eq("is_read", false),
      supabase.from("contracts").select("id", { count: "exact", head: true })
        .eq("client_id", cid).eq("tenant_id", tid),
      supabase.from("jobs").select("id", { count: "exact", head: true })
        .eq("client_id", cid).eq("tenant_id", tid),
    ]);

    if (clientRes.error || !clientRes.data) { setNotFound(true); setLoading(false); return; }
    setClient(clientRes.data as Client);
    setUnreadMessages(msgCount.count ?? 0);
    setContractsCount(contractCount.count ?? 0);
    setJobsCount(jobCount.count ?? 0);
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid && clientId) fetchClient(tid, clientId);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchClient, clientId]);

  if (loading) return <AdminLayout><LoadingState /></AdminLayout>;

  if (notFound || !client || !tenantId) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-slate-100">
            <i className="ri-user-3-line text-slate-300 text-3xl"></i>
          </div>
          <div className="text-center">
            <p className="text-slate-800 font-semibold text-lg mb-1">Client not found</p>
            <p className="text-slate-400 text-sm">This client doesn&apos;t exist or you don&apos;t have access.</p>
          </div>
          <button
            onClick={() => navigate("/admin/clients")}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 border border-slate-200 px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Clients
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="px-4 md:px-8 pt-6 pb-0">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-slate-400 mb-4">
          <button onClick={() => navigate("/admin/clients")} className="hover:text-slate-600 transition-colors cursor-pointer">
            Clients
          </button>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-slate-500 truncate max-w-[200px]">{client.full_name}</span>
        </div>

        {/* Header row */}
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="flex items-start gap-4">
            <button
              onClick={() => navigate("/admin/clients")}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-400 hover:text-slate-700 transition-all cursor-pointer flex-shrink-0 mt-0.5"
            >
              <i className="ri-arrow-left-line text-sm"></i>
            </button>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#C8A96E]/10 text-[#9a7840] font-bold text-base shrink-0">
                  {client.full_name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h1 className="text-slate-800 font-bold text-xl leading-tight">{client.full_name}</h1>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {client.email && (
                      <a href={`mailto:${client.email}`} className="text-slate-400 text-xs hover:text-slate-600 flex items-center gap-1 cursor-pointer">
                        <i className="ri-mail-line text-[10px]"></i>
                        {client.email}
                      </a>
                    )}
                    {client.phone && (
                      <>
                        <span className="text-slate-200">·</span>
                        <a href={`tel:${client.phone}`} className="text-slate-400 text-xs hover:text-slate-600 flex items-center gap-1 cursor-pointer">
                          <i className="ri-phone-line text-[10px]"></i>
                          {client.phone}
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {contractsCount !== null && (
              <div className="hidden md:flex items-center gap-1 text-slate-400 text-xs">
                <i className="ri-file-text-line text-[10px]"></i>
                {contractsCount} contracts
              </div>
            )}
            {jobsCount !== null && (
              <>
                <span className="hidden md:block text-slate-200">·</span>
                <div className="hidden md:flex items-center gap-1 text-slate-400 text-xs">
                  <i className="ri-camera-3-line text-[10px]"></i>
                  {jobsCount} jobs
                </div>
              </>
            )}
            <StatusBadge status={client.status} />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-0.5 border-b border-slate-200 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all cursor-pointer whitespace-nowrap border-b-2 -mb-px flex-shrink-0 ${
                activeTab === tab.id
                  ? "text-[#9a7840] border-[#C8A96E]"
                  : "text-slate-400 border-transparent hover:text-slate-600"
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={tab.icon}></i>
              </span>
              {tab.label}
              {tab.id === "messages" && unreadMessages > 0 && (
                <span className="bg-[#C8A96E] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                  {unreadMessages}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="px-4 md:px-8 py-6">
        {activeTab === "overview"  && <OverviewTab    client={client} tenantId={tenantId} onClientUpdated={(c) => setClient(c)} />}
        {activeTab === "portal"    && <PortalAccessTab client={client} tenantId={tenantId} />}
        {activeTab === "contracts" && <ContractsTab   client={client} tenantId={tenantId} />}
        {activeTab === "jobs"      && <JobsTab        client={client} tenantId={tenantId} />}
        {activeTab === "payments"  && <PaymentsTab    client={client} tenantId={tenantId} />}
        {activeTab === "messages"  && <MessagesTab    client={client} tenantId={tenantId} />}
        {activeTab === "addons"    && <AddOnsTab      client={client} tenantId={tenantId} />}
      </div>
    </AdminLayout>
  );
}